/**
 * @file RequireContextType.tsx
 * @description Кабинет вкладки по адресу (phase04 шаг 5; phase19.31 —
 * SC20–SC23, SC42).
 *
 * Применяется на уровне раздела: `/platform/*` — `type="PLATFORM"`,
 * `/shop/:slug/*` — `type="SHOP"`. Раньше охранник только сверял тип
 * кабинета сессии и при несовпадении молча уводил на «Выбор кабинета».
 * Теперь кабинет выбирает АДРЕС вкладки: у пользователя есть контекст
 * магазина из адреса (или контекст платформы) — он становится кабинетом
 * этой вкладки (`selectTabContext`), и все запросы раздела несут его
 * заголовком `X-Commora-Context`. Кабинет сессии при этом не меняется —
 * соседние вкладки с другими магазинами работают независимо (SC21).
 *
 * Дочерние страницы рисуются только ПОСЛЕ выбора: иначе их первые запросы
 * ушли бы ещё по старому кабинету. Выбор — в `useLayoutEffect`, до отрисовки
 * кадра, так что пустого кадра пользователь не видит.
 *
 * Во время входа под сотрудником вкладка работает только в его кабинете
 * (SC42); адрес другого кабинета уводит в кабинет сотрудника.
 */

import { useLayoutEffect } from "react";
import { Navigate, Outlet, useParams } from "react-router";

import { landingPathFor } from "../../auth/format";
import { useAuth } from "../../auth/hooks";
import { desiredTabContext, type TabContextTarget } from "../../lib/tab-context";

interface RequireContextTypeProps {
  type: "PLATFORM" | "SHOP";
}

export function RequireContextType({ type }: RequireContextTypeProps) {
  const { me, tabContextId, selectTabContext } = useAuth();
  const { slug = "" } = useParams<{ slug: string }>();
  const target: TabContextTarget = type === "SHOP" ? { type, slug } : { type };
  const desired = me ? desiredTabContext(me, target) : null;
  const desiredId = desired?.id ?? null;
  const ready = desiredId !== null && tabContextId === desiredId && me?.activeContext?.id === desiredId;

  useLayoutEffect(() => {
    if (desiredId && tabContextId !== desiredId) selectTabContext(desiredId);
  }, [desiredId, tabContextId, selectTabContext]);

  if (!me) return <Navigate to="/select-context" replace />;
  if (!desired) {
    // Под входом под сотрудником — в его кабинет; иначе доступа к кабинету
    // из адреса нет — выбор из своих кабинетов.
    const active = me.impersonation ? me.activeContext : null;
    return <Navigate to={active ? landingPathFor(active) : "/select-context"} replace />;
  }
  if (!ready) return null;
  return <Outlet />;
}
